import React, { useEffect, useState, useCallback, useContext, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Home, Warehouse, Search, XCircle, ShieldCheck, Send, Eye, RefreshCw, Copy, FileBarChart2 } from 'lucide-react';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { BRANDS, makeBrandResolver } from '@/config/stockBrands';
import { formatApiError } from '@/utils/apiError';
import logger from '@/utils/logger';

/**
 * Current Stock landing — four brand tiles (ProBound / ProEpoxy / ProDecor /
 * Miscellaneous). Tile counts roll up from the per-category aggregate.
 *
 * A global search bar sits above the tiles. When the user starts typing it
 * hides the tiles and instead renders a flat, brand-coloured product list
 * across the whole catalogue — saves drilling into a brand→category just to
 * find a single SKU.
 */
export default function StockLanding() {
  const navigate = useNavigate();
  const { isStaff, user } = useContext(AuthContext);
  const canSendDigest = user && ['admin', 'manager'].includes(user.role);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [digestOpen, setDigestOpen] = useState(false);
  const [digestRecipients, setDigestRecipients] = useState([]);      // [{email, checked}]
  const [digestLoading, setDigestLoading] = useState(false);

  // Digest preview modal — GET /api/stock/digest/preview renders the same
  // HTML the scheduler would send right now, dropped into a sandboxed iframe
  // so admins can eyeball the layout (LOW/OVER badges, reorder hints) before
  // firing an actual send.
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [previewStats, setPreviewStats] = useState(null);
  const previewIframeRef = useRef(null);
  // Copy-raw-HTML state — flips to "Copied ✓" for 1.5s after a successful
  // clipboard write so admins get feedback without a toast collision.
  const [previewCopied, setPreviewCopied] = useState(false);

  const copyPreviewHtml = useCallback(async () => {
    if (!previewHtml) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(previewHtml);
      } else {
        // Older browsers / non-secure contexts — fallback via a hidden textarea.
        const ta = document.createElement('textarea');
        ta.value = previewHtml;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setPreviewCopied(true);
      setTimeout(() => setPreviewCopied(false), 1500);
      toast.success('Digest HTML copied — paste into your marketing tool.');
    } catch (e) {
      toast.error('Copy failed — your browser blocked clipboard access.');
    }
  }, [previewHtml]);

  // Push HTML into the iframe body. Called both from the useEffect (initial
  // open) and directly after Refresh so identical byte-for-byte payloads
  // still repaint (React bails out of re-renders on primitive equality, so
  // relying on useEffect deps alone can miss a refresh).
  const writeToIframe = useCallback((html) => {
    const frame = previewIframeRef.current;
    if (!frame || !html) return;
    const doc = frame.contentDocument;
    if (!doc) return;
    doc.open();
    doc.write(`<!doctype html><html><head><meta charset="utf-8"><title>Digest preview</title></head><body style="margin:0;background:#f8fafc;padding:16px;">${html}</body></html>`);
    doc.close();
  }, []);

  const loadPreview = useCallback(async () => {
    setPreviewLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/stock/digest/preview`, { credentials: 'include' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(formatApiError(err) || 'Could not load digest preview');
      }
      const d = await res.json();
      setPreviewSubject(d.subject || '');
      setPreviewHtml(d.body_html || '');
      setPreviewStats(d.stats || null);
      // Force write after the iframe (if remounted / initial) is ready.
      // rAF makes sure the DOM has flushed the loading overlay off before
      // we touch contentDocument.
      requestAnimationFrame(() => writeToIframe(d.body_html || ''));
    } catch (e) {
      toast.error(e.message);
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  }, [writeToIframe]);

  const openPreview = () => {
    setPreviewOpen(true);
    loadPreview();
  };

  // Also handle the case where the modal opens with cached HTML (Refresh
  // isn't the only path — first open too). If the iframe has just been
  // mounted and we already have HTML in state, paint it once.
  useEffect(() => {
    if (previewOpen && previewHtml) {
      requestAnimationFrame(() => writeToIframe(previewHtml));
    }
  }, [previewOpen, previewHtml, writeToIframe]);

  const openDigestPicker = async () => {
    setDigestOpen(true);
    setDigestLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/stock/digest/recipients`, { credentials: 'include' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(formatApiError(err) || 'Could not load recipient list');
      }
      const { recipients } = await res.json();
      setDigestRecipients((recipients || []).map(e => ({ email: e, checked: true })));
    } catch (e) {
      toast.error(e.message);
      setDigestOpen(false);
    } finally {
      setDigestLoading(false);
    }
  };

  const toggleDigestRecipient = (email) => {
    setDigestRecipients(list => list.map(r => r.email === email ? { ...r, checked: !r.checked } : r));
  };
  const setAllDigest = (checked) => {
    setDigestRecipients(list => list.map(r => ({ ...r, checked })));
  };

  const sendDigestNow = async () => {
    const chosen = digestRecipients.filter(r => r.checked).map(r => r.email);
    if (chosen.length === 0) {
      toast.error('Pick at least one recipient.');
      return;
    }
    setSendingDigest(true);
    try {
      const res = await fetch(`${API_URL}/api/stock/digest/send-now`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipients: chosen }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(formatApiError(err) || 'Failed to send digest');
      }
      const d = await res.json();
      if (d.errors) {
        toast.warning(`Digest fired to ${d.sent}/${d.recipients} — ${d.errors} failed. Check Email diagnostics.`);
      } else {
        toast.success(`Digest sent to ${d.sent} recipient(s) · ${d.stats?.total_skus ?? 0} SKUs · ${d.stats?.low_count ?? 0} low`);
      }
      setDigestOpen(false);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSendingDigest(false);
    }
  };
  const isApprover = user && (user.role === 'admin' || user.role === 'manager');
  const isTeam = user?.role === 'team';
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [pendingCount, setPendingCount] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [catsRes, prodRes] = await Promise.all([
        fetch(`${API_URL}/api/stock/categories`, { credentials: 'include' }),
        fetch(`${API_URL}/api/stock/products`, { credentials: 'include' }),
      ]);
      if (!catsRes.ok) throw new Error('Failed to load categories');
      if (!prodRes.ok) throw new Error('Failed to load products');
      setCategories(await catsRes.json());
      setProducts(await prodRes.json());
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isStaff) { navigate('/'); return; }
    load();
    // Fetch pending count for the approvals badge — approvers see "all
    // pending", team sees only their own pending submissions.
    (async () => {
      try {
        if (isApprover) {
          const r = await fetch(`${API_URL}/api/stock/approvals/pending-count`, { credentials: 'include' });
          if (r.ok) setPendingCount((await r.json()).count || 0);
        } else if (isTeam) {
          const r = await fetch(`${API_URL}/api/stock/approvals?status=mine`, { credentials: 'include' });
          if (r.ok) {
            const rows = await r.json();
            setPendingCount(rows.filter(x => x.status === 'pending').length);
          }
        }
      } catch (err) {
        // Badge is best-effort — failures shouldn't block the page load.
        // We log so it shows up in browser devtools if something's actually broken.
        logger.warn('[StockLanding] pending-count fetch failed:', err);
      }
    })();
  }, [isStaff, navigate, load, isApprover, isTeam]);

  const resolveBrand = useMemo(() => makeBrandResolver(categories), [categories]);

  const brandTotals = useMemo(() => {
    const out = Object.fromEntries(BRANDS.map(b => [b.key, { skus: 0, units: 0, cats: 0 }]));
    for (const c of categories) {
      const bk = resolveBrand(c.category);
      const bucket = out[bk];
      if (!bucket) continue;
      bucket.cats += 1;
      bucket.skus += c.product_count || 0;
      bucket.units += c.total_qty || 0;
    }
    return out;
  }, [categories, resolveBrand]);

  // Filter products against the search term — case-insensitive substring match
  // on product name AND category name so "primer" finds it whether you typed
  // the product or the bucket. Empty query short-circuits to skip the work.
  const searchResults = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return [];
    return products
      .filter(p => `${p.name} ${p.category}`.toLowerCase().includes(q))
      .map(p => {
        const brandKey = resolveBrand(p.category);
        const brand = BRANDS.find(b => b.key === brandKey) || BRANDS[0];
        return { ...p, brand };
      });
  }, [products, searchTerm, resolveBrand]);

  const isSearching = searchTerm.trim().length > 0;

  if (!isStaff) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="text-white" style={{ background: 'linear-gradient(135deg, #0B0B0E 0%, #1f4030 75%)', borderBottom: '3px solid #D4A24B' }}>
        <div className="max-w-[1500px] mx-auto px-3 sm:px-4 py-4 sm:py-5 flex items-center gap-2 sm:gap-3">
          <button onClick={() => navigate('/staff')} data-testid="stock-back" className="p-2 hover:bg-white/10 rounded-lg shrink-0" aria-label="Back to Staff Dashboard">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base sm:text-2xl font-bold flex items-center gap-2 truncate"><Warehouse className="w-5 h-5 sm:w-6 sm:h-6 shrink-0" style={{ color: '#D4A24B' }} /> Current Stock</h1>
            <p className="hidden sm:block text-white/70 text-sm mt-0.5">Pick a brand to see what we hold, or search the whole catalogue</p>
          </div>
          <Button onClick={() => navigate('/staff/stock/report')} variant="outline" size="sm" className="bg-white/10 border-white/30 text-white hover:bg-white/20 shrink-0 px-2 sm:px-3" data-testid="stock-report-btn" aria-label="Stock out report">
            <FileBarChart2 className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Stock out report</span>
          </Button>
          <Button onClick={() => navigate('/staff/stock/approvals')} variant="outline" size="sm" className="bg-white/10 border-white/30 text-white hover:bg-white/20 shrink-0 px-2 sm:px-3 relative" data-testid="stock-approvals-btn" aria-label="Stock approvals">
            <ShieldCheck className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Approvals</span>
            {pendingCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-[10px] font-bold text-white flex items-center justify-center" data-testid="stock-approvals-badge">
                {pendingCount > 99 ? '99+' : pendingCount}
              </span>
            )}
          </Button>
          {canSendDigest && (
            <Button
              onClick={openPreview}
              variant="outline"
              size="sm"
              className="bg-white/10 border-white/30 text-white hover:bg-white/20 shrink-0 px-2 sm:px-3"
              data-testid="stock-preview-digest-btn"
              aria-label="Preview daily stock digest"
              title="See what today's digest would look like before sending"
            >
              <Eye className="w-4 h-4 sm:mr-1.5" />
              <span className="hidden sm:inline">Preview</span>
            </Button>
          )}
          {canSendDigest && (
            <Button
              onClick={openDigestPicker}
              variant="outline"
              size="sm"
              disabled={sendingDigest}
              className="bg-white/10 border-white/30 text-white hover:bg-white/20 shrink-0 px-2 sm:px-3"
              data-testid="stock-send-digest-btn"
              aria-label="Send daily stock digest — pick recipients"
              title="Pick recipients and send the daily stock digest email"
            >
              <Send className={`w-4 h-4 sm:mr-1.5 ${sendingDigest ? 'animate-pulse' : ''}`} />
              <span className="hidden sm:inline">{sendingDigest ? 'Sending…' : 'Send digest'}</span>
            </Button>
          )}
          <Button onClick={() => navigate('/staff')} variant="outline" size="sm" className="bg-white/10 border-white/30 text-white hover:bg-white/20 shrink-0 px-2 sm:px-3" data-testid="stock-home" aria-label="Staff">
            <Home className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Staff</span>
          </Button>
        </div>
      </div>

      <div className="max-w-[1500px] mx-auto px-4 py-5">
        {/* Global stock search — sits above the brand tiles and searches every
            product across every brand/category in one go. */}
        <div className="mb-5 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <Input
            data-testid="stock-global-search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search the whole stock catalogue by product or category…"
            className="pl-9 pr-10 h-12 text-base"
            autoFocus
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
              data-testid="stock-global-search-clear"
              aria-label="Clear search"
            >
              <XCircle className="w-5 h-5" />
            </button>
          )}
        </div>

        {loading ? (
          <div className="text-center py-16 text-gray-500">Loading…</div>
        ) : isSearching ? (
          <SearchResults results={searchResults} query={searchTerm} onPickProduct={(p) => navigate(`/staff/stock/category?name=${encodeURIComponent(p.category)}`)} />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {BRANDS.map(b => {
              const t = brandTotals[b.key] || { skus: 0, units: 0, cats: 0 };
              return (
                <button
                  key={b.key}
                  data-testid={`stock-brand-${b.key}`}
                  onClick={() => navigate(`/staff/stock/brand?key=${b.key}`)}
                  className="rounded-2xl p-6 text-left border-2 border-transparent hover:border-gray-300 hover:shadow-lg transition-all"
                  style={{ background: b.color, color: b.ink }}
                >
                  <div className="text-xs uppercase tracking-widest opacity-80">Ox Resin</div>
                  <div className="text-3xl font-bold mt-1">{b.label}</div>
                  <div className="text-sm opacity-80 mt-1">{b.blurb}</div>
                  <div className="mt-5 grid grid-cols-3 gap-2 text-center">
                    <div>
                      <div className="text-2xl font-bold">{t.cats}</div>
                      <div className="text-[10px] uppercase tracking-wider opacity-70">Categories</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold">{t.skus}</div>
                      <div className="text-[10px] uppercase tracking-wider opacity-70">SKUs</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold">{t.units}</div>
                      <div className="text-[10px] uppercase tracking-wider opacity-70">Units</div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={previewOpen} onOpenChange={(o) => setPreviewOpen(o)}>
        <DialogContent
          data-testid="digest-preview-modal"
          className="max-w-3xl w-[95vw] p-0 overflow-hidden gap-0 max-h-[92vh] flex flex-col"
        >
          <DialogHeader className="px-5 pt-5 pb-3 border-b">
            <DialogTitle>Digest preview</DialogTitle>
            <DialogDescription>
              Live render of the digest against today&apos;s catalogue. What you see here is exactly what would land in inboxes when you hit <em>Send digest</em>.
            </DialogDescription>
          </DialogHeader>

          <div className="px-5 py-3 border-b bg-gray-50 text-xs sm:text-sm flex items-center gap-3 flex-wrap">
            <span className="text-gray-500 font-medium shrink-0">Subject:</span>
            <span className="font-mono text-gray-900 flex-1 min-w-0 truncate" data-testid="digest-preview-subject">
              {previewLoading ? '…' : (previewSubject || '(no subject)')}
            </span>
            {previewStats && !previewLoading && (
              <span className="text-gray-600 flex items-center gap-2 shrink-0">
                <span>{previewStats.total_skus} SKUs</span>
                <span className={previewStats.low_count ? 'text-red-600 font-semibold' : 'text-gray-400'}>
                  {previewStats.low_count} low
                </span>
                <span className={previewStats.over_count ? 'text-amber-600' : 'text-gray-400'}>
                  {previewStats.over_count} over
                </span>
              </span>
            )}
          </div>

          <div className="flex-1 min-h-0 bg-slate-100 relative">
            <iframe
              ref={previewIframeRef}
              title="Digest preview"
              data-testid="digest-preview-iframe"
              sandbox="allow-same-origin"
              className="w-full h-full min-h-[420px] block bg-white"
              style={{ height: '60vh' }}
            />
            {previewLoading && (
              <div
                className="absolute inset-0 flex items-center justify-center bg-white/70 backdrop-blur-sm text-gray-600 italic"
                data-testid="digest-preview-loading"
              >
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Rendering current catalogue…
              </div>
            )}
          </div>

          <DialogFooter className="px-5 py-3 border-t bg-white">
            <Button
              variant="outline"
              onClick={loadPreview}
              disabled={previewLoading}
              data-testid="digest-preview-refresh"
            >
              <RefreshCw className={`w-4 h-4 mr-1.5 ${previewLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              variant="outline"
              onClick={copyPreviewHtml}
              disabled={previewLoading || !previewHtml}
              data-testid="digest-preview-copy-html"
              title="Copy the raw HTML — handy for pasting into one-off marketing sends"
            >
              <Copy className="w-4 h-4 mr-1.5" />
              {previewCopied ? 'Copied' : 'Copy HTML'}
            </Button>
            <Button
              variant="outline"
              onClick={() => setPreviewOpen(false)}
              data-testid="digest-preview-close"
            >
              Close
            </Button>
            <Button
              onClick={() => { setPreviewOpen(false); openDigestPicker(); }}
              disabled={previewLoading}
              className="bg-slate-800 hover:bg-slate-900 text-white"
              data-testid="digest-preview-send"
            >
              <Send className="w-4 h-4 mr-1.5" />
              Send this…
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={digestOpen} onOpenChange={(o) => !o && !sendingDigest && setDigestOpen(o)}>
        <DialogContent data-testid="digest-recipients-modal" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Send stock digest</DialogTitle>
            <DialogDescription>
              Tick / untick who receives this send. Uncheck anyone you want to skip — the scheduled 09:00 Mon–Fri send is unaffected.
            </DialogDescription>
          </DialogHeader>
          {digestLoading ? (
            <p className="text-sm text-gray-500 italic py-6 text-center">Loading recipients…</p>
          ) : (
            <>
              <div className="flex items-center gap-2 text-xs text-gray-600 px-1">
                <button
                  onClick={() => setAllDigest(true)}
                  className="underline hover:text-gray-900"
                  data-testid="digest-select-all"
                >Select all</button>
                <span>·</span>
                <button
                  onClick={() => setAllDigest(false)}
                  className="underline hover:text-gray-900"
                  data-testid="digest-clear-all"
                >Clear all</button>
                <span className="ml-auto font-medium text-gray-800">
                  {digestRecipients.filter(r => r.checked).length}/{digestRecipients.length} selected
                </span>
              </div>
              <div className="max-h-72 overflow-auto border rounded-lg divide-y divide-gray-100 bg-white">
                {digestRecipients.map(r => (
                  <label
                    key={r.email}
                    className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 cursor-pointer"
                    data-testid={`digest-row-${r.email}`}
                  >
                    <Checkbox checked={r.checked} onCheckedChange={() => toggleDigestRecipient(r.email)} />
                    <span className="text-sm text-gray-800 truncate">{r.email}</span>
                  </label>
                ))}
              </div>
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDigestOpen(false)} disabled={sendingDigest}>Cancel</Button>
            <Button
              onClick={sendDigestNow}
              disabled={sendingDigest || digestLoading || digestRecipients.filter(r => r.checked).length === 0}
              data-testid="digest-send-btn"
              className="bg-slate-800 hover:bg-slate-900 text-white"
            >
              <Send className={`w-4 h-4 mr-1.5 ${sendingDigest ? 'animate-pulse' : ''}`} />
              {sendingDigest ? 'Sending…' : `Send to ${digestRecipients.filter(r => r.checked).length}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SearchResults({ results, query, onPickProduct }) {
  if (results.length === 0) {
    return (
      <Card className="p-10 text-center text-gray-500 italic">
        No products match &ldquo;{query}&rdquo; — try a shorter search term.
      </Card>
    );
  }
  return (
    <>
      <div className="text-xs text-gray-500 mb-2 px-1">
        {results.length} product{results.length === 1 ? '' : 's'} matching &ldquo;{query}&rdquo;
      </div>
      <Card className="divide-y divide-gray-100">
        {results.map(p => (
          <button
            key={p.id}
            data-testid={`stock-search-row-${p.id}`}
            onClick={() => onPickProduct(p)}
            className="w-full p-3 sm:p-4 text-left hover:bg-gray-50 transition-colors"
          >
            {/* Row layout: on ≥sm, everything on one line (brand pill · name/cat · qty).
                On mobile the brand pill can eat 40%+ of a 390px screen and truncate
                the name to "Structural Reinf…" — so we stack: name+qty on line 1
                (full-width name, small qty right), brand pill + category on line 2. */}
            <div className="flex items-start justify-between gap-3 sm:hidden">
              <div className="font-semibold text-gray-900 leading-tight break-words min-w-0 flex-1">
                {p.name}
              </div>
              <div className="text-right shrink-0">
                <div className="text-[9px] uppercase text-gray-500 leading-none">On hand</div>
                <div className="text-lg font-bold leading-tight" data-testid={`stock-search-qty-${p.id}`}>{p.qty_on_hand}</div>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-1.5 sm:hidden">
              <span
                className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full shrink-0"
                style={{ background: p.brand.color, color: p.brand.ink }}
              >
                {p.brand.label}
              </span>
              <span className="text-xs text-gray-500 truncate">{p.category}</span>
            </div>

            {/* Desktop layout — unchanged from before */}
            <div className="hidden sm:flex items-center gap-3">
              <span
                className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full shrink-0"
                style={{ background: p.brand.color, color: p.brand.ink }}
              >
                {p.brand.label}
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-gray-900 truncate">{p.name}</div>
                <div className="text-xs text-gray-500 truncate">{p.category}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-[10px] uppercase text-gray-500">On hand</div>
                <div className="text-xl font-bold">{p.qty_on_hand}</div>
              </div>
            </div>
          </button>
        ))}
      </Card>
    </>
  );
}
